import {
  Component,
  ElementRef,
  Injector,
  OnInit,
  ViewChild,
  forwardRef,
  computed,
  inject,
  signal,
  effect,
  input,
  output,
  ChangeDetectionStrategy,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { ScrollingModule, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';

export type SoInputType = 'text' | 'email' | 'number' | 'password' | 'search' | 'tel' | 'url';

export interface SoInputSuggestion {
  key: string | number;
  value: string;
}

import { SoButtonGradientUI } from '../so-button-gradient/so-button-gradient.component';
import { SoAiLoaderUI } from '../so-ai-loader/so-ai-loader.component';
import { SoInputActionButton, SoInputPrefixOption } from './so-input.interfaces';

@Component({
  selector: 'so-input-ui',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ScrollingModule, OverlayModule, SoButtonGradientUI, SoAiLoaderUI],
  templateUrl: './so-input.component.html',
  styleUrl: './so-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoInputUI),
      multi: true
    }
  ]
})
export class SoInputUI implements ControlValueAccessor, OnInit {
  // Signal-based inputs
  label = input<string>('');
  placeholder = input<string>('');
  type = input<SoInputType>('text');
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  maxlength = input<number>(255);
  minlength = input<number>(0);
  autocomplete = input<string>('off');
  showNoSuggestionsMessage = input<boolean>(true);
  display = input<'vertical' | 'horizontal'>('vertical');
  
  // Styling inputs
  customStyle = input<string>('');
  labelStyle = input<string>('');
  inputHeight = input<number>(36);
  
  // Prefix support
  showPrefix = input<boolean>(false);
  prefix = input<string>('');
  prefixType = input<'text' | 'icon' | 'dropdown'>('text');
  prefixOptions = input<SoInputPrefixOption[]>([]);
  selectedPrefixKey = input<string>('');
  
  // Validation
  showErrorText = input<boolean>(true);
  
  // Password toggle
  isPassword = input<boolean>(false);
  
  // Textarea mode
  isTextarea = input<boolean>(false);
  rows = input<number>(4);
  maxRows = input<number>(6);
  
  // Suggestions/Autocomplete
  suggestions = input<SoInputSuggestion[]>([]);
  enableVirtualScroll = input<boolean>(false);
  virtualScrollMinItems = input<number>(200);
  virtualScrollItemSize = input<number>(36);
  virtualScrollMaxHeight = input<number>(240);
  
  // Action buttons (label row)
  actionButtons = input<SoInputActionButton[]>([]);
  
  // Inline action buttons (inside the input field, right side)
  inlineActionButtons = input<SoInputActionButton[]>([]);

  // Backward compatibility inputs (aliases)
  typeInput = input<SoInputType>('text');
  isDisabled = input<boolean>(false);
  requiredMark = input<boolean>(false);
  requiredField = input<boolean>(false);
  formControl = input<FormControl | null>(null);
  items = input<SoInputSuggestion[]>([]);
  isUpperCase = input<boolean>(false);
  isLowerCase = input<boolean>(false);
  onlyAlphanumeric = input<boolean>(false);
  maxNumber = input<number | null>(null);
  minNumber = input<number | null>(null);

  // Outputs
  valueChanged = output<string>();
  prefixChanged = output<string>();
  
  // ViewChild references
  @ViewChild('inputElement') inputElement?: ElementRef<HTMLInputElement>;
  @ViewChild('textareaElement') textareaElement?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('triggerContainer') triggerContainer?: ElementRef<HTMLElement>;
  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;

  // Internal signals
  protected internalValue = signal<string>('');
  private cvaDisabled = signal<boolean>(false);
  protected showPassword = signal<boolean>(false);
  protected isPanelOpen = signal<boolean>(false);
  protected activeIndex = signal<number>(-1);
  protected triggerWidth = signal<number>(0);
  protected isPrefixDropdownOpen = signal<boolean>(false);
  protected internalSelectedPrefixKey = signal<string>('');
  
  // ControlValueAccessor
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private shouldIgnoreNextBlur = false;
  private isProgrammaticUpdate = false;
  private suppressNextFocusOpen = false;

  // Overlay positions
  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
  ];

  // Computed values
  protected effectiveType = computed(() => {
    if (this.isPassword() && !this.showPassword()) return 'password';
    if (this.isPassword() && this.showPassword()) return 'text';
    return this.type() || this.typeInput() || 'text';
  });

  protected effectiveDisabled = computed(() => this.disabled() || this.isDisabled() || this.cvaDisabled());
  private injector = inject(Injector);
  private controlRequired = signal(false);
  protected effectiveRequired = computed(() => this.required() || this.requiredMark() || this.requiredField() || this.controlRequired());
  
  protected effectiveSuggestions = computed(() => {
    const items = this.suggestions().length ? this.suggestions() : this.items();
    return items || [];
  });

  protected shouldUseVirtualScroll = computed(() => {
    return this.enableVirtualScroll() || this.effectiveSuggestions().length >= this.virtualScrollMinItems();
  });

  protected filteredSuggestions = computed(() => {
    const items = this.effectiveSuggestions();
    const searchTerm = this.internalValue().toLowerCase();
    
    if (!searchTerm || !this.shouldUseVirtualScroll()) return items;
    
    return items.filter(item => 
      item.value.toLowerCase().includes(searchTerm) ||
      String(item.key).toLowerCase().includes(searchTerm)
    );
  });

  protected viewportHeight = computed(() => {
    const count = this.filteredSuggestions().length;
    if (count === 0) return this.virtualScrollItemSize();
    return Math.min(count * this.virtualScrollItemSize(), this.virtualScrollMaxHeight());
  });

  protected selectedPrefixLabel = computed(() => {
    const key = this.internalSelectedPrefixKey() || this.selectedPrefixKey();
    const options = this.prefixOptions();
    const found = options.find(opt => opt.key === key);
    return found?.label || options[0]?.label || '';
  });

  ngOnInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    queueMicrotask(() => {
      const control = ngControl?.control;
      if (control?.hasValidator) {
        this.controlRequired.set(control.hasValidator(Validators.required));
      }
    });
  }

  constructor() {
    effect(() => {
      // Keep formControl in sync if provided
      const fc = this.formControl();
      if (fc && fc.value !== this.internalValue()) {
        this.internalValue.set(fc.value || '');
      }
    }, {  });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateTriggerWidth();
  }

  // ControlValueAccessor implementation
  writeValue(value: string | null): void {
    this.internalValue.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
  }

  // Event handlers
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    let value = target.value;

    // Apply case transformations
    if (this.isUpperCase()) {
      value = value.toUpperCase();
    } else if (this.isLowerCase()) {
      value = value.toLowerCase();
    }

    this.internalValue.set(value);
    this.onChange(value);
    this.onTouched();
    this.valueChanged.emit(value);

    if (this.shouldUseVirtualScroll() && !this.isProgrammaticUpdate) {
      this.openPanel();
    }
    this.isProgrammaticUpdate = false;
  }

  onKeyPress(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const effectiveType = this.effectiveType();

    // Tel input - only allow numbers
    if (effectiveType === 'tel') {
      if (!this.isValidPhoneKey(event)) {
        event.preventDefault();
      }
    }

    // Number input - allow digits, decimal point, minus sign
    if (effectiveType === 'number') {
      const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '-', 'Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'];
      if (!allowedKeys.includes(event.key)) {
        event.preventDefault();
        return;
      }
      // Prevent multiple decimal points
      if (event.key === '.' && input.value.includes('.')) {
        event.preventDefault();
        return;
      }
      // Prevent minus sign if not at start
      if (event.key === '-' && input.selectionStart !== 0) {
        event.preventDefault();
        return;
      }
      if (input.value.length >= this.maxlength()) {
        event.preventDefault();
      }
    }

    // Only alphanumeric
    if (this.onlyAlphanumeric()) {
      const regex = /^[a-zA-Z0-9\s]$/;
      if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
        event.preventDefault();
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (this.effectiveType() === 'tel') {
      const pastedText = event.clipboardData?.getData('Text').trim() ?? '';
      if (!/^\d+$/.test(pastedText)) {
        event.preventDefault();
      }
    }
  }

  onFocus(): void {
    if (!this.shouldUseVirtualScroll()) return;
    if (this.suppressNextFocusOpen) {
      this.suppressNextFocusOpen = false;
      return;
    }
    this.openPanel();
  }

  onBlur(): void {
    this.onTouched();
    if (!this.shouldUseVirtualScroll()) return;
    if (this.shouldIgnoreNextBlur) {
      this.shouldIgnoreNextBlur = false;
      this.focusInput();
      return;
    }
    this.closePanel();
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.shouldUseVirtualScroll()) return;

    switch (event.key) {
      case 'ArrowDown':
        this.openPanel();
        this.moveActiveIndex(1);
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.openPanel();
        this.moveActiveIndex(-1);
        event.preventDefault();
        break;
      case 'Enter':
        if (this.isPanelOpen() && this.activeIndex() >= 0) {
          const item = this.filteredSuggestions()[this.activeIndex()];
          if (item) {
            event.preventDefault();
            this.selectSuggestion(item);
          }
        }
        break;
      case 'Escape':
        if (this.isPanelOpen()) {
          this.closePanel();
          event.preventDefault();
        }
        break;
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword.update(v => !v);
  }

  // Suggestion methods
  onSuggestionMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.shouldIgnoreNextBlur = true;
  }

  onSuggestionClick(item: SoInputSuggestion): void {
    this.shouldIgnoreNextBlur = false;
    this.selectSuggestion(item);
  }

  selectSuggestion(item: SoInputSuggestion): void {
    this.isProgrammaticUpdate = true;
    this.internalValue.set(item.value);
    this.onChange(item.value);
    this.valueChanged.emit(item.value);
    this.closePanel();
    this.suppressNextFocusOpen = true;
    this.focusInput();
  }

  onOverlayAttach(): void {
    this.updateTriggerWidth();
    this.scrollToActive();
  }

  onOverlayDetach(): void {
    this.activeIndex.set(-1);
  }

  onOverlayOutsideClick(): void {
    this.closePanel();
  }

  trackBySuggestion(_: number, item: SoInputSuggestion): string | number {
    return item.key;
  }

  // Private methods
  private isValidPhoneKey(event: KeyboardEvent): boolean {
    const charCode = event.key;
    const currentValue = (event.target as HTMLInputElement).value;
    const totalNumbers = currentValue.replace(/[^0-9]/g, '').length;

    if (totalNumbers >= 15 && charCode !== 'Backspace') {
      return false;
    }

    return (charCode >= '0' && charCode <= '9') || charCode === 'Backspace';
  }

  private openPanel(): void {
    if (!this.shouldUseVirtualScroll()) return;
    this.updateTriggerWidth();
    this.isPanelOpen.set(true);
    this.ensureActiveIndex();
    setTimeout(() => this.scrollToActive(), 0);
  }

  private closePanel(): void {
    if (!this.shouldUseVirtualScroll()) return;
    this.isPanelOpen.set(false);
  }

  private focusInput(): void {
    if (this.isTextarea()) {
      this.textareaElement?.nativeElement.focus();
    } else {
      this.inputElement?.nativeElement.focus();
    }
  }

  private updateTriggerWidth(): void {
    if (!this.triggerContainer) return;
    const element = this.triggerContainer.nativeElement;
    const rectWidth = element.getBoundingClientRect().width;
    const measuredWidth = Math.max(rectWidth, element.clientWidth, element.offsetWidth);
    this.triggerWidth.set(Math.ceil(measuredWidth));
  }

  togglePrefixDropdown(): void {
    this.isPrefixDropdownOpen.update(v => !v);
  }

  closePrefixDropdown(): void {
    this.isPrefixDropdownOpen.set(false);
  }

  selectPrefixOption(option: SoInputPrefixOption): void {
    this.internalSelectedPrefixKey.set(option.key);
    this.prefixChanged.emit(option.key);
    this.closePrefixDropdown();
    this.internalValue.set('');
    this.onChange('');
    this.valueChanged.emit('');
    this.focusInput();
  }

  private ensureActiveIndex(): void {
    const items = this.filteredSuggestions();
    if (!items.length) {
      this.activeIndex.set(-1);
      return;
    }
    const currentValue = this.internalValue();
    const matchIndex = items.findIndex(item => item.value === currentValue);
    this.activeIndex.set(matchIndex >= 0 ? matchIndex : 0);
    this.scrollToActive();
  }

  private moveActiveIndex(step: number): void {
    const items = this.filteredSuggestions();
    if (!items.length) {
      this.activeIndex.set(-1);
      return;
    }
    const itemCount = items.length;
    const current = this.activeIndex();
    const nextIndex = current < 0 
      ? (step > 0 ? 0 : itemCount - 1) 
      : (current + step + itemCount) % itemCount;
    this.activeIndex.set(nextIndex);
    this.scrollToActive();
  }

  private scrollToActive(): void {
    if (!this.viewport || this.activeIndex() < 0) return;
    this.viewport.scrollToIndex(this.activeIndex(), 'smooth');
  }
}
